import { Middleware } from '@reduxjs/toolkit';

import {
  confirmPaymentFailure,
  fillPaymentForm,
  initiatePaymentForm,
  initiateProcessing,
  processPaymentFailure,
  processPaymentStart,
  processPaymentSuccess,
  registerPaymentFailure,
  registerPaymentStart,
  registerPaymentSuccess,
  setPaymentFormStatus,
} from '@cashier/cashier-data-access';
import {
  FormStatuses,
  PaymentActionError,
  Versions,
} from '@cashier/cashier-model';
import { getCurrencySymbol } from '@cashier/cashier-shared';

import {
  FormFieldProvider,
  FormPayloadFactory,
  FormSchemaFactory,
} from '../utils';

export const paymentFormMiddleware: Middleware =
  (store) => (next) => (action) => {
    const result = next(action);

    if (action.type === initiatePaymentForm.toString()) {
      const { account, amount } = action.payload;

      const {
        cashierConfiguration: {
          currency,
          amount: staticAmount,
          platform,
          paymentType,
          version,
        },
        paymentMethod: { paymentMethod, accounts, country },
        customerInformation: { balance },
      } = store.getState();

      const {
        id: paymentMethodId,
        categories,
        ruleParams,
        description: paymentMethodDescription,
        show_account_form,
      } = paymentMethod;

      const enrichedFields = FormFieldProvider.provideEnriched(
        paymentMethod,
        account,
        show_account_form ? accounts : [],
        { country, platform },
      );

      // initiate processing without submitting a form
      if (
        !paymentMethodDescription &&
        !enrichedFields.some((field) => field.required) &&
        version !== Versions.v3
      ) {
        store.dispatch(initiateProcessing());
      } else {
        const schema = FormSchemaFactory.create(enrichedFields, {
          paymentType,
          currency: getCurrencySymbol(currency) || null,
          isAmountStatic: !!staticAmount,
          initialAmount: amount,
          paymentMethod: {
            id: paymentMethodId,
            categories,
            min: ruleParams?.min || null,
            max: ruleParams?.max || null,
            amounts: ruleParams?.amounts || [],
            step: ruleParams?.step || null,
          },
        });

        const payload = FormPayloadFactory.fromFormSchema(schema, {
          paymentType,
          balance,
        });

        store.dispatch(
          fillPaymentForm({
            version: crypto.randomUUID(),
            schema,
            payload,
          }),
        );
      }
    }

    if (
      [
        processPaymentStart.toString(),
        registerPaymentStart.toString(),
      ].includes(action.type)
    ) {
      store.dispatch(setPaymentFormStatus(FormStatuses.SUBMITTED));
    }

    if (
      [
        processPaymentSuccess.toString(),
        registerPaymentSuccess.toString(),
      ].includes(action.type)
    ) {
      store.dispatch(setPaymentFormStatus(FormStatuses.SUCCEEDED));
    }

    if (
      [
        processPaymentFailure.toString(),
        registerPaymentFailure.toString(),
        confirmPaymentFailure.toString(),
      ].includes(action.type)
    ) {
      const { context }: PaymentActionError = action.payload;

      store.dispatch(
        setPaymentFormStatus(
          context?.ui.display === 'field'
            ? FormStatuses.READY
            : FormStatuses.FAILED,
        ),
      );
    }

    return result;
  };
